// Kennungen der E-Rechnung prüfen – EINE Quelle für Kundenstamm (masterdata), Betriebsdaten (platform), die
// Vorprüfung vor dem Festschreiben (Modul einvoice, P-03/P-06/P-07/P-12) und später die Formulare.
// Spezifikation: docs/erechnung/SPEZIFIKATION.md §5.2–§5.4. Alle Prüfungen sind rein (Browser und Node), liefern
// `null` für „in Ordnung“ oder einen fertigen deutschen Satz für Menschen.
//
// Quellen: BR-DE-28 (E-Mail) und die EAS-Liste stammen aus der XRechnung-Schematron 2.6.0 (common.sch,
// SHA-256 ca5e07af…, §3.1), die VATEX-Codes aus der CEN-Validierung der KoSIT-Konfiguration 2026-08-31 (BR-CL-22,
// SHA-256 2530cd10…, §3.1) – beide am 15.10.2026 gelesen. Präfixe und Muster der USt-IdNr. [U: VIES, nicht am
// Original gelesen], Aufbau der Leitweg-ID [U: Formatspezifikation der KoSIT] und der Gläubiger-ID [U: Deutsche
// Bundesbank] wie in der Spezifikation gekennzeichnet.

import { brand } from './brand'

// ---- Auswahllisten ----------------------------------------------------------------------------------------------

/** Syntax der XRechnung (E1): UBL 2.1 oder UN/CEFACT CII D16B. */
export const EINVOICE_SYNTAXES = ['ubl', 'cii'] as const
export type EInvoiceSyntax = (typeof EINVOICE_SYNTAXES)[number]
export const EINVOICE_SYNTAX_LABELS: Record<EInvoiceSyntax, string> = {
  ubl: 'UBL (Standard)',
  cii: 'CII (UN/CEFACT)',
}

/** Zahlart des Kunden (BT-81, §4.10). */
export const PAYMENT_METHODS = ['transfer', 'direct_debit', 'cash'] as const
export type PartnerPaymentMethod = (typeof PAYMENT_METHODS)[number]
export const PAYMENT_METHOD_LABELS: Record<PartnerPaymentMethod, string> = {
  transfer: 'Überweisung',
  direct_debit: 'SEPA-Lastschrift',
  cash: 'Barzahlung',
}

/** Schemata elektronischer Adressen, die Merxo annimmt (§5.3; alle in der EAS-Liste, BR-CL-25). */
export const EINVOICE_ADDRESS_SCHEMES = ['EM', '0204', '9930', '0088', '0060'] as const
export type EInvoiceAddressScheme = (typeof EINVOICE_ADDRESS_SCHEMES)[number]
export const EINVOICE_ADDRESS_SCHEME_LABELS: Record<EInvoiceAddressScheme, string> = {
  EM: 'E-Mail',
  '0204': 'Leitweg-ID',
  '9930': 'deutsche USt-IdNr.',
  '0088': 'GLN',
  '0060': 'DUNS-Nummer',
}

// ---- Prüfziffern ------------------------------------------------------------------------------------------------

/** Buchstaben → Zahlen wie bei der IBAN (A = 10 … Z = 35); Ziffern bleiben. */
const toDigits = (value: string): string =>
  [...value].map((ch) => (ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch)).join('')

/** Rest einer beliebig langen Ziffernfolge modulo 97 (ohne Gleitkomma, ohne BigInt). */
export function mod97(digits: string): number {
  let rest = 0
  for (const digit of digits) rest = (rest * 10 + Number(digit)) % 97
  return rest
}

/** Prüfziffer nach GS1 (GTIN/GLN): gewichtet 3-1 von rechts, über die Ziffern OHNE Prüfziffer. */
export function gs1CheckDigit(body: string): number {
  let sum = 0
  for (let index = 0; index < body.length; index++) {
    const digit = Number(body[body.length - 1 - index])
    sum += index % 2 === 0 ? digit * 3 : digit
  }
  return (10 - (sum % 10)) % 10
}

/** IBAN nach ISO 13616 (Modulo 97). Leerraum und Kleinbuchstaben sind erlaubt. */
export function isValidIban(raw: string): boolean {
  const iban = raw.replace(/\s+/g, '').toUpperCase()
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(iban)) return false
  return mod97(toDigits(iban.slice(4) + iban.slice(0, 4))) === 1
}

// ---- GLN ---------------------------------------------------------------------------------------------------------

export function glnProblem(raw: string): string | null {
  const gln = raw.replace(/\s+/g, '')
  if (!/^\d{13}$/.test(gln)) return 'Eine GLN hat 13 Ziffern, die letzte ist die Prüfziffer.'
  if (gs1CheckDigit(gln.slice(0, 12)) !== Number(gln[12])) return 'Die Prüfziffer der GLN stimmt nicht. Bitte prüfen Sie die Eingabe.'
  return null
}

// ---- E-Mail (BR-DE-28) --------------------------------------------------------------------------------------------

/** Regex der XRechnung-Schematron 2.6.0 (`XR-EMAIL-REGEX`, BR-DE-28): genau ein @, Domäne mit mindestens einem Punkt. */
export const EINVOICE_EMAIL_PATTERN = /^[^@\s]+@([^@.\s]+\.)+[^@.\s]+$/

export const isValidEInvoiceEmail = (value: string): boolean => EINVOICE_EMAIL_PATTERN.test(value.trim())

// ---- Leitweg-ID (BT-10, EAS 0204) -------------------------------------------------------------------------------

/** Grobadressierung (2–12 Ziffern) · optional Feinadressierung (1–30 Zeichen 0-9/A-Z) · zwei Prüfziffern. */
export const LEITWEG_ID_PATTERN = /^\d{2,12}(-[0-9A-Z]{1,30})?-\d{2}$/

/** Schreibweise für Vergleich und Ablage: ohne Leerraum, Buchstaben groß. */
export const normalizeLeitwegId = (raw: string): string => raw.replace(/\s+/g, '').toUpperCase()

/**
 * Leitweg-ID mit Prüfziffer nach ISO 7064 MOD 97-10 über alle Zeichen ohne Bindestriche (Rest = 1) –
 * Beispiel `04011000-1234512345-06` (§5.2 Nr. 3).
 */
export function leitwegIdProblem(raw: string): string | null {
  const id = normalizeLeitwegId(raw)
  if (!LEITWEG_ID_PATTERN.test(id)) {
    return 'Eine Leitweg-ID hat die Form Grobadressierung-Feinadressierung-Prüfziffer, z. B. 04011000-1234512345-06.'
  }
  if (mod97(toDigits(id.replace(/-/g, ''))) !== 1) return 'Die Prüfziffer der Leitweg-ID stimmt nicht. Bitte prüfen Sie die Eingabe.'
  return null
}

// ---- Gläubiger-Identifikationsnummer (BT-90) --------------------------------------------------------------------

/** DE + 2 Prüfziffern + 3 Zeichen Geschäftsbereichskennung (meist ZZZ, frei wählbar) + 11 Ziffern (§5.4). */
export const CREDITOR_ID_PATTERN = /^DE\d{2}[A-Z0-9]{3}\d{11}$/

export const normalizeCreditorId = (raw: string): string => raw.replace(/\s+/g, '').toUpperCase()

/**
 * Prüfziffern nach ISO 7064 MOD 97-10 über nationale Kennung + Länderkennzeichen + „00“, OHNE die
 * Geschäftsbereichskennung (Prüfziffer = 98 − Rest). Nachgerechnet: `DE98ZZZ09999999999`.
 */
export function creditorIdProblem(raw: string): string | null {
  const id = normalizeCreditorId(raw)
  if (!CREDITOR_ID_PATTERN.test(id)) {
    return 'Eine Gläubiger-ID hat die Form DE, zwei Prüfziffern, drei Zeichen Geschäftsbereich und elf Ziffern, z. B. DE98ZZZ09999999999.'
  }
  const expected = 98 - mod97(toDigits(`${id.slice(7)}${id.slice(0, 2)}00`))
  if (expected !== Number(id.slice(2, 4))) return 'Die Prüfziffer der Gläubiger-ID stimmt nicht. Bitte prüfen Sie die Eingabe.'
  return null
}

// ---- USt-IdNr. ----------------------------------------------------------------------------------------------------

/**
 * EU-Mitgliedstaaten (ISO 3166-1) → Präfix der USt-IdNr. Griechenland führt `EL`. Nordirland (Anschrift im
 * Vereinigten Königreich, Warenverkehr) führt `XI` – siehe `vatIdPrefixesFor`. [U: VIES]
 */
export const EU_VAT_PREFIX: Readonly<Record<string, string>> = {
  AT: 'AT', BE: 'BE', BG: 'BG', CY: 'CY', CZ: 'CZ', DE: 'DE', DK: 'DK', EE: 'EE', ES: 'ES', FI: 'FI', FR: 'FR',
  GR: 'EL', HR: 'HR', HU: 'HU', IE: 'IE', IT: 'IT', LT: 'LT', LU: 'LU', LV: 'LV', MT: 'MT', NL: 'NL', PL: 'PL',
  PT: 'PT', RO: 'RO', SE: 'SE', SI: 'SI', SK: 'SK',
} // prettier-ignore

/** Liegt das Land (ISO 3166-1) in der EU? */
export const isEuCountry = (country: string | null | undefined): boolean => Boolean(country && EU_VAT_PREFIX[country.toUpperCase()])

/** Präfixe, die zu einer Anschrift in diesem Land passen (leer: kein EU-Präfix bekannt). */
export function vatIdPrefixesFor(country: string | null | undefined): string[] {
  const code = (country ?? '').toUpperCase()
  if (code === 'GB') return ['XI']
  const prefix = EU_VAT_PREFIX[code]
  return prefix ? [prefix] : []
}

/** Muster je Präfix (inkl. Präfix) mit einem Beispiel für die Meldung. [U: VIES-Formate] */
export const VAT_ID_FORMATS: Readonly<Record<string, { pattern: RegExp; example: string }>> = {
  AT: { pattern: /^ATU\d{8}$/, example: 'ATU12345678' },
  BE: { pattern: /^BE[01]\d{9}$/, example: 'BE0123456789' },
  BG: { pattern: /^BG\d{9,10}$/, example: 'BG123456789' },
  CY: { pattern: /^CY\d{8}[A-Z]$/, example: 'CY12345678X' },
  CZ: { pattern: /^CZ\d{8,10}$/, example: 'CZ12345678' },
  DE: { pattern: /^DE\d{9}$/, example: 'DE123456789' },
  DK: { pattern: /^DK\d{8}$/, example: 'DK12345678' },
  EE: { pattern: /^EE\d{9}$/, example: 'EE123456789' },
  EL: { pattern: /^EL\d{9}$/, example: 'EL123456789' },
  ES: { pattern: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/, example: 'ESX1234567X' },
  FI: { pattern: /^FI\d{8}$/, example: 'FI12345678' },
  FR: { pattern: /^FR[A-HJ-NP-Z0-9]{2}\d{9}$/, example: 'FR12345678901' },
  HR: { pattern: /^HR\d{11}$/, example: 'HR12345678901' },
  HU: { pattern: /^HU\d{8}$/, example: 'HU12345678' },
  IE: { pattern: /^IE(\d{7}[A-W][A-I]?|\d[A-Z+*]\d{5}[A-W])$/, example: 'IE1234567T' },
  IT: { pattern: /^IT\d{11}$/, example: 'IT12345678901' },
  LT: { pattern: /^LT(\d{9}|\d{12})$/, example: 'LT123456789' },
  LU: { pattern: /^LU\d{8}$/, example: 'LU12345678' },
  LV: { pattern: /^LV\d{11}$/, example: 'LV12345678901' },
  MT: { pattern: /^MT\d{8}$/, example: 'MT12345678' },
  NL: { pattern: /^NL\d{9}B\d{2}$/, example: 'NL123456789B01' },
  PL: { pattern: /^PL\d{10}$/, example: 'PL1234567890' },
  PT: { pattern: /^PT\d{9}$/, example: 'PT123456789' },
  RO: { pattern: /^RO\d{2,10}$/, example: 'RO1234567890' },
  SE: { pattern: /^SE\d{10}01$/, example: 'SE123456789001' },
  SI: { pattern: /^SI\d{8}$/, example: 'SI12345678' },
  SK: { pattern: /^SK\d{10}$/, example: 'SK1234567890' },
  XI: { pattern: /^XI(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/, example: 'XI123456789' },
}

/** Schreibweise für Ablage und Vergleich: ohne Leerraum, Punkte und Bindestriche, Buchstaben groß. */
export const normalizeVatId = (raw: string): string => raw.replace(/[\s.-]+/g, '').toUpperCase()

/**
 * Form der USt-IdNr.: Präfix aus der Tabelle oben, dann das Muster des Präfixes. Präfixe außerhalb der EU (CH, GB,
 * NO …) prüft Merxo nur grob. Ob sie PFLICHT ist, entscheidet nicht das Speichern, sondern die Vorprüfung vor dem
 * Festschreiben – nur Kategorie K und AE verlangen sie (BR-IC-02, BR-AE-02; §5.2 Nr. 7).
 */
export function vatIdProblem(raw: string): string | null {
  const id = normalizeVatId(raw)
  if (!/^[A-Z]{2}[A-Z0-9+*]{2,13}$/.test(id)) return 'Eine USt-IdNr. beginnt mit dem Länderkürzel, z. B. DE123456789.'
  const prefix = id.slice(0, 2)
  if (prefix === 'GR') return 'Griechische USt-IdNr. beginnen mit EL, z. B. EL123456789.'
  const format = VAT_ID_FORMATS[prefix]
  if (!format || format.pattern.test(id)) return null
  if (prefix === 'DE') return 'Eine deutsche USt-IdNr. hat die Form DE und neun Ziffern.'
  return `Eine USt-IdNr. mit dem Präfix ${prefix} hat die Form ${format.example}.`
}

// ---- Elektronische Adresse (BT-34, BT-49; §5.3) ------------------------------------------------------------------

export type EInvoiceAddress = { scheme: EInvoiceAddressScheme; id: string }

/**
 * Ein Textfeld: mit `@` eine E-Mail-Adresse (Schema EM), sonst `<EAS-Code>:<Kennung>` mit 0204 (Leitweg-ID),
 * 9930 (deutsche USt-IdNr.), 0088 (GLN) oder 0060 (DUNS). Ergebnis: Schema und Kennung – oder ein deutscher Satz.
 */
export function parseEInvoiceAddress(raw: string): { ok: true; address: EInvoiceAddress } | { ok: false; message: string } {
  const value = raw.trim()
  if (value.includes('@')) {
    return isValidEInvoiceEmail(value)
      ? { ok: true, address: { scheme: 'EM', id: value } }
      : { ok: false, message: 'Die E-Mail-Adresse für E-Rechnungen ist nicht gültig (genau ein @ und eine Domäne mit Punkt).' }
  }
  const match = /^(\d{4})\s*:\s*(\S.*)$/.exec(value)
  const usage =
    'Bitte eine E-Mail-Adresse oder eine Kennung mit Schema angeben, z. B. 0204:04011000-1234512345-06 (Leitweg-ID), ' +
    '9930:DE123456789, 0088:<GLN> oder 0060:<DUNS>.'
  if (!match) return { ok: false, message: usage }
  const scheme = match[1] as string
  const id = (match[2] as string).replace(/\s+/g, '')
  switch (scheme) {
    case '0204': {
      const problem = leitwegIdProblem(id)
      return problem ? { ok: false, message: problem } : { ok: true, address: { scheme, id: normalizeLeitwegId(id) } }
    }
    case '9930': {
      const vat = normalizeVatId(id)
      return /^DE\d{9}$/.test(vat)
        ? { ok: true, address: { scheme, id: vat } }
        : { ok: false, message: 'Nach 9930: steht eine deutsche USt-IdNr. (DE und neun Ziffern).' }
    }
    case '0088': {
      const problem = glnProblem(id)
      return problem ? { ok: false, message: problem } : { ok: true, address: { scheme, id } }
    }
    case '0060':
      return /^\d{9}$/.test(id)
        ? { ok: true, address: { scheme, id } }
        : { ok: false, message: 'Eine DUNS-Nummer hat neun Ziffern.' }
    default:
      return { ok: false, message: `Das Schema ${scheme} nimmt ${brand.name} nicht an. ${usage}` }
  }
}

export function einvoiceAddressProblem(raw: string): string | null {
  const parsed = parseEInvoiceAddress(raw)
  return parsed.ok ? null : parsed.message
}

/** Gespeicherte Schreibweise: E-Mail wie eingegeben (getrimmt), sonst `<Schema>:<Kennung>` ohne Leerraum. */
export function normalizeEInvoiceAddress(raw: string): string {
  const parsed = parseEInvoiceAddress(raw)
  if (!parsed.ok) return raw.trim()
  return parsed.address.scheme === 'EM' ? parsed.address.id : `${parsed.address.scheme}:${parsed.address.id}`
}

// ---- SEPA-Lastschriftmandat (BG-19) --------------------------------------------------------------------------------

/** Mandatsreferenz: höchstens 35 Zeichen aus dem SEPA-Zeichensatz, ohne Leerzeichen. [U: Regelwerk der Kreditwirtschaft] */
export const MANDATE_REFERENCE_PATTERN = /^[A-Za-z0-9+?/\-:().,']{1,35}$/

export function mandateReferenceProblem(raw: string): string | null {
  return MANDATE_REFERENCE_PATTERN.test(raw.trim())
    ? null
    : 'Die Mandatsreferenz hat höchstens 35 Zeichen: Buchstaben, Ziffern und + ? / - : ( ) . , \' – ohne Leerzeichen.'
}

// ---- Befreiungsgrund (BT-121, VATEX) -------------------------------------------------------------------------------

/**
 * VATEX-Codes der EU aus der CEN-Validierung (BR-CL-22) der KoSIT-Konfiguration 2026-08-31. Die französischen
 * Codes derselben Liste (VATEX-FR-…) gelten nur für Rechnungen französischer Verkäufer und sind hier nicht wählbar.
 */
export const VATEX_CODES = [
  'VATEX-EU-79-C',
  'VATEX-EU-132', 'VATEX-EU-132-1A', 'VATEX-EU-132-1B', 'VATEX-EU-132-1C', 'VATEX-EU-132-1D', 'VATEX-EU-132-1E',
  'VATEX-EU-132-1F', 'VATEX-EU-132-1G', 'VATEX-EU-132-1H', 'VATEX-EU-132-1I', 'VATEX-EU-132-1J', 'VATEX-EU-132-1K',
  'VATEX-EU-132-1L', 'VATEX-EU-132-1M', 'VATEX-EU-132-1N', 'VATEX-EU-132-1O', 'VATEX-EU-132-1P', 'VATEX-EU-132-1Q',
  'VATEX-EU-135-1',
  'VATEX-EU-143', 'VATEX-EU-143-1A', 'VATEX-EU-143-1B', 'VATEX-EU-143-1C', 'VATEX-EU-143-1D', 'VATEX-EU-143-1E',
  'VATEX-EU-143-1F', 'VATEX-EU-143-1FA', 'VATEX-EU-143-1G', 'VATEX-EU-143-1H', 'VATEX-EU-143-1I', 'VATEX-EU-143-1J',
  'VATEX-EU-143-1K', 'VATEX-EU-143-1L',
  'VATEX-EU-144', 'VATEX-EU-146-1E', 'VATEX-EU-159', 'VATEX-EU-309',
  'VATEX-EU-148', 'VATEX-EU-148-A', 'VATEX-EU-148-B', 'VATEX-EU-148-C', 'VATEX-EU-148-D', 'VATEX-EU-148-E',
  'VATEX-EU-148-F', 'VATEX-EU-148-G',
  'VATEX-EU-151', 'VATEX-EU-151-1A', 'VATEX-EU-151-1AA', 'VATEX-EU-151-1B', 'VATEX-EU-151-1C', 'VATEX-EU-151-1D',
  'VATEX-EU-151-1E',
  'VATEX-EU-153',
  'VATEX-EU-G', 'VATEX-EU-O', 'VATEX-EU-IC', 'VATEX-EU-AE', 'VATEX-EU-D', 'VATEX-EU-F', 'VATEX-EU-I', 'VATEX-EU-J',
] as const // prettier-ignore
export type VatexCode = (typeof VATEX_CODES)[number]

/** Codes, die eine eigene Kategorie festlegen (§4.11): Grund und Kategorie gehören zusammen. */
export const VATEX_CATEGORY: Readonly<Partial<Record<VatexCode, 'AE' | 'K' | 'G' | 'O'>>> = {
  'VATEX-EU-AE': 'AE',
  'VATEX-EU-IC': 'K',
  'VATEX-EU-G': 'G',
  'VATEX-EU-O': 'O',
}

const VATEX_TOPIC: Readonly<Partial<Record<string, string>>> = {
  'VATEX-EU-79-C': 'durchlaufende Posten, Auslagenersatz',
  'VATEX-EU-132': 'dem Gemeinwohl dienende Tätigkeiten',
  'VATEX-EU-135-1': 'andere steuerbefreite Tätigkeiten',
  'VATEX-EU-143': 'Einfuhr',
  'VATEX-EU-144': 'Dienstleistungen im Zusammenhang mit der Einfuhr',
  'VATEX-EU-146-1E': 'Dienstleistungen im Zusammenhang mit der Ausfuhr',
  'VATEX-EU-148': 'internationale See- und Luftfahrt',
  'VATEX-EU-151': 'diplomatische und gleichgestellte Umsätze',
  'VATEX-EU-153': 'Leistungen von Vermittlern',
  'VATEX-EU-309': 'Reiseleistungen außerhalb der EU',
}

const VATEX_SPECIAL: Readonly<Partial<Record<VatexCode, string>>> = {
  'VATEX-EU-AE': 'Steuerschuldnerschaft des Leistungsempfängers',
  'VATEX-EU-IC': 'Innergemeinschaftliche Lieferung',
  'VATEX-EU-G': 'Ausfuhr aus der EU',
  'VATEX-EU-O': 'Nicht steuerbar',
  'VATEX-EU-D': 'Differenzbesteuerung – Reisebüros',
  'VATEX-EU-F': 'Differenzbesteuerung – Gebrauchtgegenstände',
  'VATEX-EU-I': 'Differenzbesteuerung – Kunstgegenstände',
  'VATEX-EU-J': 'Differenzbesteuerung – Sammlungsstücke und Antiquitäten',
}

/** „VATEX-EU-132-1A – Art. 132 Abs. 1 Buchst. a MwStSystRL“ – Anzeige in Auswahllisten. */
export function vatexLabel(code: VatexCode): string {
  const special = VATEX_SPECIAL[code]
  if (special) return `${code} – ${special}`
  const [, article = '', rest = ''] = /^VATEX-EU-(\d+)-?(.*)$/.exec(code) ?? []
  const paragraph = /^(\d)([A-Z]*)$/.exec(rest)
  let text = `Art. ${article}`
  if (paragraph) text += ` Abs. ${paragraph[1]}${paragraph[2] ? ` Buchst. ${paragraph[2].toLowerCase()}` : ''}`
  else if (rest) text += ` Buchst. ${rest.toLowerCase()}`
  const topic = VATEX_TOPIC[code] ?? VATEX_TOPIC[`VATEX-EU-${article}`]
  return `${code} – ${text} MwStSystRL${topic ? ` (${topic})` : ''}`
}

export const isVatexCode = (value: string): value is VatexCode => (VATEX_CODES as readonly string[]).includes(value)

/**
 * Passt der Befreiungsgrund zur Kategorie des Steuerschlüssels (§5.2 Nr. 8)? AE/K/G/O haben genau ihren Code,
 * alle übrigen VATEX-Codes gehören zu E; S und Z tragen keinen.
 */
export function exemptionCodeProblem(category: string, code: string | null | undefined): string | null {
  if (!code) return null
  if (!isVatexCode(code)) return `${code} ist kein Befreiungsgrund der VATEX-Liste.`
  const fixed = VATEX_CATEGORY[code]
  if (category === 'S' || category === 'Z') return 'Steuerpflichtige Schlüssel (Kategorie S und Z) tragen keinen Befreiungsgrund.'
  if (fixed) return fixed === category ? null : `${code} gehört zur Kategorie ${fixed}, nicht zu ${category}.`
  if (category === 'E') return null
  const own = Object.entries(VATEX_CATEGORY).find(([, value]) => value === category)?.[0]
  return `Zur Kategorie ${category} passt nur der Befreiungsgrund ${own ?? '– keiner –'}.`
}
